import { X } from 'lucide-react'
import { formatDateTime } from '../../../../shared/lib/format'
import type { ApiKey, ApiPermissionOption } from './types'

interface EditApiKeyModalProps {
  apiKey: ApiKey | null
  keyName: string
  onKeyNameChange: (value: string) => void
  permissions: string[]
  onPermissionsChange: (value: string[]) => void
  isActive: boolean
  onIsActiveChange: (value: boolean) => void
  availablePermissions: ApiPermissionOption[]
  isUpdating: boolean
  onCancel: () => void
  onSubmit: () => void
}

/** API 키 편집 */
export default function EditApiKeyModal({
  apiKey,
  keyName,
  onKeyNameChange,
  permissions,
  onPermissionsChange,
  isActive,
  onIsActiveChange,
  availablePermissions,
  isUpdating,
  onCancel,
  onSubmit,
}: EditApiKeyModalProps) {
  if (!apiKey) {
    return null
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="m-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 p-6 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            API 키 편집
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <div className="space-y-6 p-6">
          {/* 사용자 정보 (읽기 전용) */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              사용자
            </label>
            <div className="rounded-lg bg-gray-50 p-3 dark:bg-gray-700">
              <div className="font-medium text-gray-900 dark:text-white">
                {apiKey.userId.name || apiKey.userId.email}
              </div>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                {apiKey.userId.email} - {apiKey.userId.plan} 플랜
              </div>
            </div>
          </div>

          {/* 키 이름 */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              API 키 이름 *
            </label>
            <input
              type="text"
              value={keyName}
              onChange={e => onKeyNameChange(e.target.value)}
              placeholder="예: Production API Key"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
            />
          </div>

          {/* 권한 선택 */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              권한 선택 *
            </label>
            <div className="space-y-2">
              {availablePermissions.map(permission => (
                <label key={permission.id} className="flex items-start">
                  <input
                    type="checkbox"
                    checked={permissions.includes(permission.id)}
                    onChange={e => {
                      if (e.target.checked) {
                        onPermissionsChange([...permissions, permission.id])
                      } else {
                        onPermissionsChange(
                          permissions.filter(p => p !== permission.id)
                        )
                      }
                    }}
                    className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-2">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {permission.name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {permission.description}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* 활성 상태 */}
          <div>
            <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
              상태
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={isActive}
                onChange={e => onIsActiveChange(e.target.checked)}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="ml-2 text-sm text-gray-900 dark:text-white">
                API 키 활성화
              </span>
            </label>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              비활성화하면 이 API 키로 요청할 수 없습니다.
            </p>
          </div>

          {/* 키 정보 */}
          <div className="rounded-lg bg-blue-50 p-4 dark:bg-blue-900/20">
            <h4 className="mb-2 font-medium text-gray-900 dark:text-white">
              키 정보
            </h4>
            <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
              <div>
                <strong>키 ID:</strong>{' '}
                <code className="font-mono">{apiKey.keyId}</code>
              </div>
              <div>
                <strong>생성일:</strong> {formatDateTime(apiKey.createdAt)}
              </div>
              {apiKey.lastUsed && (
                <div>
                  <strong>마지막 사용:</strong>{' '}
                  {formatDateTime(apiKey.lastUsed)}
                </div>
              )}
              {apiKey.expiresAt && (
                <div>
                  <strong>만료일:</strong> {formatDateTime(apiKey.expiresAt)}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end space-x-3 border-t border-gray-200 p-6 dark:border-gray-700">
          <button
            onClick={onCancel}
            className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
          >
            취소
          </button>
          <button
            onClick={onSubmit}
            disabled={isUpdating || !keyName.trim() || permissions.length === 0}
            className="rounded-lg border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isUpdating ? '수정 중...' : '수정'}
          </button>
        </div>
      </div>
    </div>
  )
}
